export const kycFields = [
  ["arrivalDate", "Arrival date"],
  ["givenName", "Given name"],
  ["middleName", "Middle name"],
  ["surname", "Surname"],
  ["dob", "Date of birth"],
  ["passportNumber", "Passport number"],
  ["passportCountry", "Issuing country"],
  ["passportExpiryDate", "Passport expiry date"],
] as const;

export type KycField = (typeof kycFields)[number][0];
export type KycDetails = Record<KycField, string>;
export type KycActivationOptions = {
  useCustomerKyc?: boolean;
  customerEmail?: string;
};
export type KycRow = KycDetails &
  KycActivationOptions & {
    iccid: string;
  };
export type KycStatus =
  | "not_submitted"
  | "processing"
  | "submitted"
  | "rejected"
  | "needs_review";
export type KycSummary = {
  kycSupported: boolean;
  passportRequired: boolean;
  canSubmitKyc: boolean;
  kycUnavailableReason?: string | null;
  kycStatus: KycStatus;
  kycMessage?: string | null;
  arrivalDate?: string | null;
};
export type KycInventoryRow = KycSummary & {
  iccid: string;
  simplyProductCode?: string | null;
  orderId?: string;
  simIdx?: string;
  status?: string;
  activationCodeUrl?: string;
};
export type KycResult = Pick<
  KycInventoryRow,
  "iccid" | "kycStatus" | "kycMessage"
>;
export type KycRowError = { row: number; iccid: string; message: string };

export const kycStatusLabels: Record<KycStatus, string> = {
  not_submitted: "Not submitted",
  processing: "Processing",
  submitted: "Submitted",
  rejected: "Rejected",
  needs_review: "Submission needs review",
};

export const emptyKycDetails = (): KycDetails =>
  Object.fromEntries(kycFields.map(([field]) => [field, ""])) as KycDetails;

export function isDateField(field: string) {
  return ["arrivalDate", "dob", "passportExpiryDate"].includes(field);
}

export function validCalendarDate(value: string) {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) {
    return false;
  }
  const date = new Date(`${value}T00:00:00Z`);
  return (
    !Number.isNaN(date.getTime()) && date.toISOString().slice(0, 10) === value
  );
}

export function arrivalToday() {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: "Australia/Sydney",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(new Date());
  const get = (name: string) => parts.find((part) => part.type === name)?.value;
  return `${get("year")}-${get("month")}-${get("day")}`;
}

export function requiresKycIdentity(row: KycDetails & KycActivationOptions) {
  return (
    row.useCustomerKyc !== undefined &&
    (row.useCustomerKyc ||
      kycFields.some(
        ([field]) => field !== "arrivalDate" && Boolean(row[field]?.trim()),
      ))
  );
}

export function validateKycRow(row: KycRow, item?: KycSummary): string[] {
  const errors: string[] = [];
  if (!/^[A-Za-z0-9]{8,22}$/.test(row.iccid)) {
    errors.push("ICCID must be text containing 8–22 letters or digits.");
  }
  if (!item) {
    errors.push("ICCID is not available in your inventory.");
  } else if (!item.canSubmitKyc) {
    errors.push(
      item.kycUnavailableReason ||
        "KYC submission is not available for this ICCID.",
    );
  }
  for (const [field, label] of kycFields) {
    const value = row[field]?.trim() || "";
    if (
      (field === "arrivalDate" ||
        (item?.passportRequired && field !== "middleName") ||
        (requiresKycIdentity(row) &&
          field !== "middleName" &&
          field !== "passportExpiryDate")) &&
      !value
    ) {
      errors.push(`${label} is required.`);
    }
    if (value.length > 200) {
      errors.push(`${label} must have at most 200 characters.`);
    }
    if (value && isDateField(field) && !validCalendarDate(value)) {
      errors.push(`${label} must be a valid date in YYYY-MM-DD format.`);
    }
  }
  if (validCalendarDate(row.arrivalDate) && row.arrivalDate < arrivalToday()) {
    errors.push("Arrival date must be today or later (Australia/Sydney).");
  }
  if (row.useCustomerKyc) {
    const email = row.customerEmail?.trim() || "";
    if (!email) {
      errors.push("Customer email is required.");
    } else if (
      email.length > 200 ||
      !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)
    ) {
      errors.push(
        "Customer email must be a valid email address " +
          "with at most 200 characters.",
      );
    }
  }
  return errors;
}
